import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtDate, formatApiErrorDetail, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader } from "@/components/Bits";
import MonthEndBar from "@/components/MonthEndBar";
import MonthEndJourney from "@/components/MonthEndJourney";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle, XCircle, Plus, Trash, LockSimple, LockSimpleOpen, SealCheck, Calendar, DownloadSimple, ArrowRight, WarningCircle, ClockCounterClockwise, CalendarPlus, ArrowUUpLeft, ArrowCounterClockwise, Lightning, CloudArrowDown, DotOutline, CaretRight } from "@phosphor-icons/react";

const ACTIVITY_META = {
  schedule: { Icon: CalendarPlus, cls: "text-orangeink", ring: "bg-orange/10", label: "Scheduled" },
  reverse: { Icon: ArrowUUpLeft, cls: "text-amber-600", ring: "bg-amber-500/10", label: "Reversed" },
  delete: { Icon: Trash, cls: "text-negative", ring: "bg-negative/10", label: "Deleted" },
  restore: { Icon: ArrowCounterClockwise, cls: "text-positive", ring: "bg-positive/10", label: "Restored" },
  rule: { Icon: Lightning, cls: "text-teal-600", ring: "bg-teal-500/10", label: "Rule applied" },
  import: { Icon: CloudArrowDown, cls: "text-navy/70", ring: "bg-navy/10", label: "Imported" },
  event: { Icon: DotOutline, cls: "text-navy/70", ring: "bg-navy/5", label: "Event" },
};

export default function Close() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [periods, setPeriods] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showJournals, setShowJournals] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/close-periods");
      setPeriods(data.periods || []);
    } finally { setLoaded(true); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Re-open the close window the user came from after resolving an outstanding task.
  useEffect(() => {
    const openId = searchParams.get("open");
    const newClose = searchParams.get("newclose");
    if (openId) setSelected(openId);
    else if (newClose) setShowNew(true);
    if (openId || newClose) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <PageHeader overline="Period close" title="Month-end Close"
          subtitle="Clear the outstanding tasks, then sign off the period per organisation." />
        <div className="flex items-center gap-2">
          <button onClick={() => setShowJournals(true)} data-testid="posted-journals-btn"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">
            <ClockCounterClockwise size={16} /> Posted journals report
          </button>
          <button onClick={() => setShowNew(true)} data-testid="new-close-btn"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors">
            <Plus size={16} /> New close period
          </button>
        </div>
      </div>

      <LockBanner />

      <div className="mb-6" data-testid="close-month-end-bar">
        <MonthEndBar />
      </div>

      <MonthEndTopStrip />

      {!loaded ? (
        <div className="py-16 text-center font-mono text-navy/60 text-sm tracking-widest" data-testid="close-loading">LOADING_DATA…</div>
      ) : periods.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="close-empty">
          <Calendar size={40} className="text-navy/30 mx-auto mb-3" />
          <div className="font-heading font-bold text-lg text-navy">No close periods yet</div>
          <div className="text-sm text-navy/70 mt-1">Create one to start a month-end checklist and sign-off.</div>
        </div>
      ) : (
        <div className="space-y-8" data-testid="close-list">
          <PeriodSection title="Current Period" testid="current-periods"
            periods={periods.filter((p) => p.status !== "Closed")}
            emptyText="No open period for this organisation — create one to start a month-end checklist."
            onSelect={setSelected} />
          <PeriodSection title="Closed Periods" testid="closed-periods"
            periods={periods.filter((p) => p.status === "Closed")}
            emptyText="No closed periods yet."
            onSelect={setSelected} />
        </div>
      )}

      <NewCloseDialog open={showNew} onClose={() => setShowNew(false)} onCreated={(id) => { load(); setSelected(id); }} />
      <PostedJournalsDialog open={showJournals} onClose={() => setShowJournals(false)} />
      {selected && (
        <CloseDetailDialog id={selected} isAdmin={isAdmin} onClose={() => setSelected(null)} onChanged={load} />
      )}
    </Page>
  );
}

function MonthEndTopStrip() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [toPost, setToPost] = useState(null);
  const [posting, setPosting] = useState(false);
  const [showToPost, setShowToPost] = useState(false);

  const load = useCallback(() => {
    api.get("/dashboard/overview").then(({ data }) => setD(data)).catch(() => {});
    api.get("/reports/journals-to-post").then(({ data }) => setToPost(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const postNow = async () => {
    setPosting(true);
    try {
      const { data: r } = await api.post("/scheduler/run", { end: "" });
      toast.success(r.queued ? `${r.posted} posted, ${r.queued} sent for approval` : `${r.posted} journal(s) posted to Xero`);
      setShowToPost(false);
      load(); window.dispatchEvent(new Event("badges:refresh"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not post journals");
    } finally { setPosting(false); }
  };

  if (!d || !d.has_tenant) return null;
  const count = toPost?.count || 0;

  return (
    <div className="space-y-4" data-testid="close-top-strip">
      <MonthEndJourney data={d} postCount={count}
        onStep={(s) => { if (s.key === "post") setShowToPost(true); else navigate(s.to); }} />
      {count > 0 && (
        <div data-testid="close-to-post-card"
          className="flex flex-wrap items-center gap-3 rounded-2xl border border-orange/30 bg-orange/[0.04] px-5 py-4">
          <SealCheck size={22} weight="fill" className="text-orangeink shrink-0" />
          <div className="min-w-0">
            <div className="font-heading font-bold text-navy">Journals to post</div>
            <div className="text-sm text-navy/70">
              <span className="font-mono font-semibold text-navy" data-testid="close-to-post-count">{count}</span> journal{count === 1 ? "" : "s"} due or overdue{toPost?.total != null ? ` · ${money(toPost.total)}` : ""} — this is what Post now will send to Xero.
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setShowToPost(true)} data-testid="close-to-post-review"
              className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 text-navy hover:border-orange hover:text-orangeink transition-colors">
              Review
            </button>
            <button onClick={postNow} disabled={posting} data-testid="close-to-post-now"
              className="px-5 py-2 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
              {posting ? "POSTING…" : "Post now"}
            </button>
          </div>
        </div>
      )}
      <JournalsToPostDialog open={showToPost} data={toPost} posting={posting}
        onPost={postNow} onClose={() => setShowToPost(false)} />
    </div>
  );
}

function PeriodSection({ title, periods, emptyText, onSelect, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className="overline text-navy/70">{title}</div>
        <span className="text-xs font-mono text-navy/65">{periods.length}</span>
      </div>
      {periods.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-lg p-6 text-sm text-navy/65" data-testid={`${testid}-empty`}>
          {emptyText}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {periods.map((p) => (
            <button key={p.id} data-testid={`close-card-${p.id}`} onClick={() => onSelect(p.id)}
              className="text-left bg-white border border-slate-200 rounded-lg p-5 hover:border-orange transition-colors">
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold text-navy">{p.title}</div>
                <StatusPill status={p.status} />
              </div>
              <div className="text-sm text-navy/70 mt-1">{p.tenant_name}</div>
              <div className="font-mono text-xs text-navy/65 mt-2">{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</div>
              {p.status === "Closed" && (
                <div className="text-xs text-positive mt-2">Signed off by {p.signed_off_by} · {fmtDate(p.signed_off_at)}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CloseReadiness({ endDate, closeCtx }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [toPost, setToPost] = useState(null);
  const [posting, setPosting] = useState(false);
  const [showToPost, setShowToPost] = useState(false);
  const load = useCallback(() => {
    api.get("/dashboard/overview").then(({ data }) => setD(data)).catch(() => {});
    api.get(`/reports/journals-to-post${endDate ? `?end=${endDate}` : ""}`).then(({ data }) => setToPost(data)).catch(() => {});
  }, [endDate]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const postNow = async () => {
    setPosting(true);
    try {
      const { data: r } = await api.post("/scheduler/run", { end: endDate || "" });
      toast.success(r.queued ? `${r.posted} posted, ${r.queued} sent for approval` : `${r.posted} journal(s) posted to Xero`);
      setShowToPost(false);
      load(); window.dispatchEvent(new Event("badges:refresh"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not post journals");
    } finally { setPosting(false); }
  };

  const tasks = [
    { key: "unscheduled", label: "Unscheduled items to process", count: d.unscheduled_count || 0, to: "/unscheduled" },
    { key: "overdue", label: "Journals to post", count: (toPost?.count) || 0, amount: toPost?.total, action: "post" },
    { key: "approvals", label: "Journals awaiting approval", count: d.approvals_waiting || 0, to: "/approvals" },
    { key: "prepay", label: "Prepayment variances to resolve", count: d.prepayment_variances || 0, to: "/reconciliation?tab=prepayment" },
    { key: "definc", label: "Deferred income variances to resolve", count: d.deferred_income_variances || 0, to: "/reconciliation?tab=deferred-income" },
    { key: "accrual", label: "Accrual variances to resolve", count: d.accrual_variances || 0, to: "/reconciliation?tab=accrual" },
    { key: "deprec", label: "Depreciation variances to resolve", count: d.depreciation_variances || 0, to: "/reconciliation?tab=depreciation" },
    { key: "recurring", label: "Recurring-accrual suggestions to review", count: d.recurring_suggestions || 0, to: "/recurring-accruals" },
  ];
  const outstanding = tasks.filter((t) => t.count > 0).length;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="close-readiness">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="overline text-orangeink">Before you close</div>
          <div className="font-heading font-bold text-lg text-navy">Outstanding tasks for this entity</div>
        </div>
        {outstanding === 0
          ? <span className="inline-flex items-center gap-1.5 text-sm font-medium text-positive" data-testid="readiness-clear"><CheckCircle size={18} weight="fill" /> All clear to close</span>
          : <span className="inline-flex items-center gap-1.5 text-sm font-medium text-warn" data-testid="readiness-count"><WarningCircle size={18} weight="fill" /> {outstanding} task type(s) outstanding</span>}
      </div>
      <div className="divide-y divide-slate-100">
        {tasks.map((t) => {
          const done = t.count === 0;
          return (
            <div key={t.key} data-testid={`readiness-${t.key}`} className="flex items-center gap-3 py-2.5">
              {done ? <CheckCircle size={20} weight="fill" className="text-positive shrink-0" />
                    : <WarningCircle size={20} weight="fill" className="text-warn shrink-0" />}
              <span className={`flex-1 text-sm ${done ? "text-navy/65" : "text-navy font-medium"}`}>{t.label}</span>
              {!done && (
                <span className="font-mono text-sm text-navy tabular" data-testid={`readiness-${t.key}-count`}>
                  {t.count}{t.amount != null ? ` · ${money(t.amount)}` : ""}
                </span>
              )}
              {!done && (t.action === "post" ? (
                <>
                  <button onClick={() => setShowToPost(true)} data-testid="readiness-review-to-post"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy hover:border-orange hover:text-orangeink transition-colors">
                    Review
                  </button>
                  <button onClick={postNow} disabled={posting} data-testid="readiness-post-now"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
                    {posting ? "POSTING…" : "Post now"}
                  </button>
                </>
              ) : (
                <button onClick={() => navigate(closeCtx ? `${t.to}${t.to.includes("?") ? "&" : "?"}fromClose=${closeCtx}` : t.to)} data-testid={`readiness-${t.key}-go`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy hover:border-orange hover:text-orangeink transition-colors">
                  Review <ArrowRight size={13} />
                </button>
              ))}
              {done && <span className="text-xs text-navy/35">Clear</span>}
            </div>
          );
        })}
      </div>
      <JournalsToPostDialog open={showToPost} data={toPost} posting={posting}
        onPost={postNow} onClose={() => setShowToPost(false)} />
    </div>
  );
}

function JournalsToPostDialog({ open, data, posting, onPost, onClose }) {
  const navigate = useNavigate();
  const catLabel = (c) => c === "DEFERRED_INCOME" ? "Deferred Income" : (c || "").charAt(0) + (c || "").slice(1).toLowerCase();
  const TYPE_ROUTE = { PREPAYMENT: "/prepayments", ACCRUAL: "/accruals", DEFERRED_INCOME: "/deferred-income", DEPRECIATION: "/fixed-assets" };
  const openSchedule = (j) => {
    const base = TYPE_ROUTE[j.category] || "/register";
    const url = j.schedule_id && base !== "/fixed-assets" ? `${base}?open=${j.schedule_id}` : base;
    onClose();
    navigate(url);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-lg border-slate-200 max-h-[92vh] overflow-y-auto" data-testid="to-post-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-xl text-navy">Journals to post</DialogTitle>
          <DialogDescription className="text-navy/70">
            {data?.tenant_name ? `${data.tenant_name} · ` : ""}Pending journals up to the open close period{data?.cutoff ? ` (to ${fmtDate(data.cutoff)})` : ""} — this is exactly what Post now will send to Xero (future periods are held; items Xero has rejected are flagged below, and go to Approvals first if 2-person approval is on). Click a row to inspect its schedule.
          </DialogDescription>
        </DialogHeader>
        <div className="border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 overline text-navy/65">
                <th className="px-3 py-2.5 text-left">Date</th><th className="px-3 py-2.5 text-left">Type</th>
                <th className="px-3 py-2.5 text-left">Description</th><th className="px-3 py-2.5 text-left">Code</th>
                <th className="px-3 py-2.5 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(data?.journals || []).map((j, i) => (
                <tr key={i} data-testid={`to-post-row-${i}`} onClick={() => openSchedule(j)}
                  title="Open this schedule on the register"
                  className="group border-b border-slate-100 last:border-0 cursor-pointer hover:bg-orange/[0.04] transition-colors">
                  <td className="px-3 py-2.5 font-mono text-xs text-navy/70">{fmtDate(j.date)}</td>
                  <td className="px-3 py-2.5">{catLabel(j.category)}
                    {j.catch_up && <span className="ml-1.5 text-[0.55rem] font-semibold uppercase px-1.5 py-0.5 rounded-full bg-orange/15 text-orangeink">Catch-up</span>}</td>
                  <td className="px-3 py-2.5 max-w-[220px] truncate" title={j.description}>{j.description}
                    {j.reference ? <div className="text-xs text-navy/65 font-mono">{j.reference}</div> : null}
                    {j.post_error ? <div className="text-xs text-negative mt-0.5" title={j.post_error}>⚠ Xero rejected: {j.post_error}</div> : null}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-navy/70">{j.source_account_code}{j.pl_account_code ? ` → ${j.pl_account_code}` : ""}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold tabular">
                    <span className="inline-flex items-center gap-1.5 justify-end">
                      {money(j.amount)}
                      <CaretRight size={13} weight="bold" className="text-slate-300 group-hover:text-orangeink group-hover:translate-x-0.5 transition-all" />
                    </span>
                  </td>
                </tr>
              ))}
              {(data?.journals || []).length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-navy/70" data-testid="to-post-empty">Nothing to post right now.</td></tr>
              )}
            </tbody>
            {(data?.journals || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-navy/20 font-semibold text-navy">
                  <td className="px-3 py-2.5" colSpan={3}>Total · {data.count} journal(s)</td>
                  <td /><td className="px-3 py-2.5 text-right font-mono tabular" data-testid="to-post-total">{money(data.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-5 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Close</button>
          <button onClick={onPost} disabled={posting || (data?.count || 0) === 0} data-testid="to-post-post-now"
            className="inline-flex items-center gap-1.5 px-6 py-2 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
            {posting ? "POSTING…" : "Post now"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PostedJournalsDialog({ open, onClose, fixedStart, fixedEnd, title }) {
  const monthStart = new Date().toISOString().slice(0, 8) + "01";
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(fixedStart || monthStart);
  const [end, setEnd] = useState(fixedEnd || today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/reports/posted-journals?start=${start}&end=${end}`);
      setData(res);
    } catch (e) { toast.error("Could not load posted journals"); }
    finally { setLoading(false); }
  }, [start, end]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const dl = async (fmt) => {
    try {
      const res = await api.get(`/reports/posted-journals/${fmt}?start=${start}&end=${end}`, { responseType: "blob" });
      const type = fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url; a.download = `posted-journals-${today}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the report"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-lg border-slate-200 max-h-[92vh] overflow-y-auto" data-testid="posted-journals-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-xl text-navy">{title || "Posted journals report"}</DialogTitle>
          <DialogDescription className="text-navy/70">
            {data?.tenant_name ? `${data.tenant_name} · ` : ""}All journals posted to Xero in the selected period (opening balances excluded).
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <span className="overline text-navy/70 block mb-1">From</span>
            <input type="date" value={start} data-testid="pj-start" onChange={(e) => setStart(e.target.value)} disabled={!!fixedStart}
              className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm font-mono outline-none focus:border-orange disabled:bg-slate-50" />
          </div>
          <div>
            <span className="overline text-navy/70 block mb-1">To</span>
            <input type="date" value={end} data-testid="pj-end" onChange={(e) => setEnd(e.target.value)} disabled={!!fixedEnd}
              className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm font-mono outline-none focus:border-orange disabled:bg-slate-50" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => dl("pdf")} data-testid="pj-pdf" className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5"><DownloadSimple size={15} /> PDF</button>
            <button onClick={() => dl("excel")} data-testid="pj-excel" className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5"><DownloadSimple size={15} /> Excel</button>
          </div>
        </div>

        <div className="mt-3 border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 overline text-navy/65">
                <th className="px-3 py-2.5 text-left">Date</th><th className="px-3 py-2.5 text-left">Type</th>
                <th className="px-3 py-2.5 text-left">Description</th><th className="px-3 py-2.5 text-left">Code</th>
                <th className="px-3 py-2.5 text-right">Amount</th><th className="px-3 py-2.5 text-left">Xero ref</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="py-8 text-center font-mono text-navy/65 text-sm">LOADING…</td></tr>}
              {!loading && (data?.journals || []).map((j, i) => (
                <tr key={i} data-testid={`pj-row-${i}`} className="border-b border-slate-100 last:border-0">
                  <td className="px-3 py-2.5 font-mono text-xs text-navy/70">{fmtDate(j.date)}</td>
                  <td className="px-3 py-2.5">{j.category === "DEFERRED_INCOME" ? "Deferred Income" : (j.category || "").charAt(0) + (j.category || "").slice(1).toLowerCase()}
                    {j.catch_up && <span className="ml-1.5 text-[0.55rem] font-semibold uppercase px-1.5 py-0.5 rounded-full bg-orange/15 text-orangeink">Catch-up</span>}</td>
                  <td className="px-3 py-2.5 max-w-[240px] truncate" title={j.description}>{j.description}
                    {j.reference ? <div className="text-xs text-navy/65 font-mono">{j.reference}</div> : null}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-navy/70">{j.source_account_code}{j.pl_account_code ? ` → ${j.pl_account_code}` : ""}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold tabular">{money(j.amount)}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-navy/70">{j.xero_ref}</td>
                </tr>
              ))}
              {!loading && (data?.journals || []).length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-navy/70" data-testid="pj-empty">No journals posted in this period.</td></tr>
              )}
            </tbody>
            {!loading && (data?.journals || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-navy/20 font-semibold text-navy">
                  <td className="px-3 py-2.5" colSpan={4}>Total · {data.count} journal(s)</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular" data-testid="pj-total">{money(data.total)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LockBanner() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [lock, setLock] = useState(null);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/lock-status");
      setLock(data);
      setVal(data.manual_lock_date || "");
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!lock) return null;

  const save = async (clear = false) => {
    setBusy(true);
    try {
      const { data } = await api.put("/lock-date", { manual_lock_date: clear ? null : (val || null) });
      setLock(data); setVal(data.manual_lock_date || ""); setEditing(false);
      toast.success(clear ? "App lock date cleared" : "App lock date set");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update lock date");
    } finally { setBusy(false); }
  };

  const xeroLocked = lock.source === "xero";
  const eff = lock.effective_lock_date;
  return (
    <div data-testid="lock-banner" className="bg-white border border-slate-200 rounded-lg p-4 flex items-start justify-between gap-4 flex-wrap">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${eff ? "bg-orange/10 text-orangeink" : "bg-navy/5 text-navy/65"}`}>
          <LockSimple size={18} weight="fill" />
        </div>
        <div>
          <div className="font-semibold text-navy text-sm">Period lock date</div>
          {eff ? (
            <div className="text-sm text-navy/70 mt-0.5" data-testid="lock-effective">
              Locked to <span className="font-mono font-medium text-navy">{fmtDate(eff)}</span>
              <span className="ml-2 text-[0.65rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-navy/[0.06] text-navy/70">
                {xeroLocked ? "from Xero" : "app lock"}</span>
            </div>
          ) : (
            <div className="text-sm text-navy/70 mt-0.5" data-testid="lock-effective">No lock date — journals can post in any period.</div>
          )}
          <div className="text-xs text-navy/65 mt-1">
            {xeroLocked
              ? "Sourced from Xero's period lock date (read-only). Journals on or before this date are blocked."
              : "App-only lock — set one here when Xero has no period lock date."}
          </div>
        </div>
      </div>
      {isAdmin && !xeroLocked && (
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <input type="date" value={val} onChange={(e) => setVal(e.target.value)} data-testid="lock-date-input"
                className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
              <button onClick={() => save(false)} disabled={busy} data-testid="lock-date-save"
                className="px-3 py-1.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">Save</button>
              <button onClick={() => { setEditing(false); setVal(lock.manual_lock_date || ""); }}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Cancel</button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} data-testid="lock-date-edit"
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">
                {eff ? "Change" : "Set lock date"}</button>
              {eff && (
                <button onClick={() => save(true)} disabled={busy} data-testid="lock-date-clear"
                  className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-navy/70 hover:bg-navy/5 hover:text-negative transition-colors disabled:opacity-60">Clear</button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const closed = status === "Closed";
  return (
    <span data-testid="close-status-pill" className={`text-[0.65rem] font-semibold uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1 ${
      closed ? "bg-positive/10 text-positive" : "bg-orange/10 text-orangeink"}`}>
      {closed ? <LockSimple size={11} /> : <LockSimpleOpen size={11} />} {status}
    </span>
  );
}

const NEW_CLOSE_DRAFT_KEY = "closeNewDraft";

function NewCloseDialog({ open, onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const draft = (() => { try { return JSON.parse(sessionStorage.getItem(NEW_CLOSE_DRAFT_KEY) || "null"); } catch { return null; } })();
  const [title, setTitle] = useState(draft?.title ?? "");
  const [start, setStart] = useState(draft?.start ?? monthStart);
  const [end, setEnd] = useState(draft?.end ?? today);
  const [saving, setSaving] = useState(false);
  const [packing, setPacking] = useState(false);

  // Keep a draft while the dialog is open so a "Review a task" round-trip restores the entry.
  useEffect(() => {
    if (open) sessionStorage.setItem(NEW_CLOSE_DRAFT_KEY, JSON.stringify({ title, start, end }));
  }, [open, title, start, end]);

  // On open with no saved draft, prefill the suggested period (first close = earliest import month).
  useEffect(() => {
    if (!open || sessionStorage.getItem(NEW_CLOSE_DRAFT_KEY)) return;
    api.get("/close-default").then(({ data }) => {
      if (data.title) setTitle(data.title);
      if (data.start_date) setStart(data.start_date);
      if (data.end_date) setEnd(data.end_date);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const clearAndReset = () => {
    sessionStorage.removeItem(NEW_CLOSE_DRAFT_KEY);
    setTitle(""); setStart(monthStart); setEnd(today);
  };
  const handleClose = () => { clearAndReset(); onClose(); };

  const submit = async () => {
    if (!title.trim()) { toast.error("Please enter a title for the close period"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/close-periods", { title: title.trim(), start_date: start, end_date: end });
      toast.success("Close period created");
      clearAndReset();
      onCreated?.(data.period.id);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create close period");
    } finally { setSaving(false); }
  };

  const downloadPreviewPack = async () => {
    setPacking(true);
    try {
      const qs = `start=${start}&end=${end}&title=${encodeURIComponent(title.trim())}`;
      const res = await api.get(`/close-pack/preview.pdf?${qs}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `close-pack-preview-${(title.trim() || end).slice(0, 24)}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Could not build the report pack");
    } finally { setPacking(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-lg border-slate-200 max-h-[92vh] overflow-y-auto" data-testid="new-close-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-xl text-navy">New close period</DialogTitle>
          <DialogDescription className="text-navy/70">For the active organisation. Clear the outstanding tasks below before you close.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Title">
            <input value={title} data-testid="close-title-input" onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. June 2026 close" className="cl-in" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date"><input type="date" value={start} data-testid="close-start-input"
              onChange={(e) => setStart(e.target.value)} className="cl-in font-mono" /></Field>
            <Field label="End date"><input type="date" value={end} data-testid="close-end-input"
              onChange={(e) => setEnd(e.target.value)} className="cl-in font-mono" /></Field>
          </div>
          <CloseReadiness endDate={end} closeCtx="new" />
        </div>
        <div className="flex items-center justify-between gap-2 pt-2">
          <button onClick={downloadPreviewPack} disabled={packing} data-testid="close-preview-pack-btn"
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:border-orange hover:text-orangeink transition-colors disabled:opacity-60">
            <DownloadSimple size={16} /> {packing ? "BUILDING…" : "Run period report pack"}
          </button>
          <div className="flex items-center gap-2">
            <button onClick={handleClose} data-testid="close-cancel-btn" className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Cancel</button>
            <button onClick={submit} disabled={saving || !title.trim()} data-testid="close-create-submit"
              className="px-5 py-2 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
              {saving ? "CREATING…" : "Create"}
            </button>
          </div>
        </div>
        <style>{`.cl-in{width:100%;background:#fff;border:1px solid rgba(11,26,47,.2);border-radius:4px;padding:.5rem .65rem;font-size:.85rem;color:#0E0F3F;outline:none}.cl-in:focus{border-color:#F37A24;box-shadow:0 0 0 2px rgba(46,163,253,.25)}`}</style>
      </DialogContent>
    </Dialog>
  );
}

function CloseDetailDialog({ id, isAdmin, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newItem, setNewItem] = useState("");
  const [showJournals, setShowJournals] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/close-periods/${id}`);
    setDetail(data);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const period = detail?.period;
  const auto = detail?.auto_items || [];
  const closed = period?.status === "Closed";

  const patchManual = async (items) => {
    const { data } = await api.patch(`/close-periods/${id}`, { manual_items: items });
    setDetail(data);
    onChanged?.();
  };

  const toggleManual = (mid) => {
    if (closed) return;
    patchManual(period.manual_items.map((m) => m.id === mid ? { ...m, done: !m.done } : m));
  };
  const removeManual = (mid) => patchManual(period.manual_items.filter((m) => m.id !== mid));
  const addManual = () => {
    if (!newItem.trim()) return;
    patchManual([...(period.manual_items || []), { label: newItem.trim(), done: false }]);
    setNewItem("");
  };

  const signoff = async () => {
    setBusy(true);
    try {
      await api.post(`/close-periods/${id}/signoff`);
      toast.success("Period signed off and closed");
      load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not sign off");
    } finally { setBusy(false); }
  };
  const reopen = async () => {
    setBusy(true);
    try {
      await api.post(`/close-periods/${id}/reopen`);
      toast.success("Period re-opened");
      load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not re-open");
    } finally { setBusy(false); }
  };

  const allDone = auto.every((a) => a.done) && (period?.manual_items || []).every((m) => m.done);

  const downloadPack = async () => {
    try {
      const res = await api.get(`/close-periods/${id}/pack.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `close-pack-${(period?.title || id).slice(0, 24)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Could not download the evidence pack");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl rounded-lg border-slate-200 max-h-[92vh] overflow-y-auto" data-testid="close-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">{period && <StatusPill status={period.status} />}</div>
          <DialogTitle className="font-heading font-bold text-xl text-navy">{period?.title || "Close period"}</DialogTitle>
          <DialogDescription className="text-navy/70">
            {period ? `${period.tenant_name} · ${fmtDate(period.start_date)} → ${fmtDate(period.end_date)}` : "Loading…"}
          </DialogDescription>
        </DialogHeader>
        {!period ? <div className="py-10 text-center font-mono text-sm text-navy/70">LOADING…</div> : (
          <>
            {period.status === "Open" && <div className="mb-4"><CloseReadiness endDate={period.end_date?.slice(0, 10)} closeCtx={id} /></div>}
            <button onClick={() => setShowJournals(true)} data-testid="period-posted-journals-btn"
              className="w-full flex items-center justify-center gap-1.5 mb-4 px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 text-navy hover:border-orange hover:text-orangeink transition-colors">
              <ClockCounterClockwise size={15} /> Posted journals for this period
            </button>
            {showJournals && (
              <PostedJournalsDialog open onClose={() => setShowJournals(false)}
                fixedStart={period.start_date?.slice(0, 10)} fixedEnd={period.end_date?.slice(0, 10)}
                title={`Posted journals · ${period.title}`} />
            )}
            <div className="space-y-1">
              <div className="overline text-navy/65 mb-1">Automated checks</div>
              {auto.map((a) => (
                <div key={a.key} data-testid={`auto-item-${a.key}`} className="flex items-start gap-2.5 py-2 border-b border-navy/5">
                  {a.done ? <CheckCircle size={20} weight="fill" className="text-positive shrink-0 mt-0.5" />
                          : <XCircle size={20} weight="fill" className="text-negative shrink-0 mt-0.5" />}
                  <div>
                    <div className="text-sm font-medium text-navy">{a.label}</div>
                    <div className="text-xs text-navy/70">{a.detail}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1 mt-4">
              <div className="overline text-navy/65 mb-1">Manual checklist</div>
              {(period.manual_items || []).map((m) => (
                <div key={m.id} data-testid={`manual-item-${m.id}`} className="flex items-center gap-2.5 py-1.5 group">
                  <button onClick={() => toggleManual(m.id)} disabled={closed} data-testid={`manual-toggle-${m.id}`}
                    className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                      m.done ? "bg-positive border-positive text-white" : "border-navy/30 hover:border-orange"} disabled:opacity-60`}>
                    {m.done && <CheckCircle size={14} weight="fill" />}
                  </button>
                  <span className={`flex-1 text-sm ${m.done ? "line-through text-navy/65" : "text-navy"}`}>{m.label}</span>
                  {!closed && (
                    <button onClick={() => removeManual(m.id)} data-testid={`manual-remove-${m.id}`}
                      className="opacity-0 group-hover:opacity-100 text-navy/30 hover:text-negative transition-all"><Trash size={15} /></button>
                  )}
                </div>
              ))}
              {(period.manual_items || []).length === 0 && <div className="text-xs text-navy/65 py-1">No manual items yet.</div>}
              {!closed && (
                <div className="flex items-center gap-2 pt-2">
                  <input value={newItem} data-testid="manual-add-input" onChange={(e) => setNewItem(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addManual()} placeholder="Add a checklist item…"
                    className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
                  <button onClick={addManual} data-testid="manual-add-btn"
                    className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Add</button>
                </div>
              )}
            </div>

            {(period.activity || []).length > 0 && (
              <div className="space-y-1 mt-4" data-testid="close-activity">
                <div className="overline text-navy/65 mb-1">Activity</div>
                {[...period.activity].reverse().map((a, i) => {
                  const meta = ACTIVITY_META[a.kind] || ACTIVITY_META.event;
                  const AIcon = meta.Icon;
                  return (
                  <div key={i} data-testid={`close-activity-item-${a.kind || "event"}`} className="flex items-start gap-2.5 py-1.5 text-sm border-b border-navy/5 last:border-0">
                    <span className={`shrink-0 mt-0.5 h-6 w-6 rounded-full flex items-center justify-center ${meta.ring}`}>
                      <AIcon size={14} weight="bold" className={meta.cls} />
                    </span>
                    <div>
                      <div className="text-navy">{a.text}</div>
                      <div className="text-xs text-navy/65">{meta.label} · {fmtDate(a.ts)}</div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-5 mt-2 border-t border-slate-100">
              {closed ? (
                <>
                  <div className="text-xs text-positive">Signed off by {period.signed_off_by} · {fmtDate(period.signed_off_at)}</div>
                  <div className="flex items-center gap-2">
                    <button onClick={downloadPack} data-testid="close-pack-btn"
                      className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">
                      <DownloadSimple size={16} /> Evidence pack
                    </button>
                    {isAdmin && (
                      <button onClick={reopen} disabled={busy} data-testid="close-reopen-btn"
                        className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 disabled:opacity-60">
                        <LockSimpleOpen size={16} /> Re-open
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-xs text-navy/70">{allDone ? "All checks complete." : "Some checks are still outstanding."}</div>
                  {isAdmin ? (
                    <button onClick={signoff} disabled={busy} data-testid="close-signoff-btn"
                      className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium rounded-lg bg-positive text-white hover:bg-navy transition-colors disabled:opacity-60">
                      <SealCheck size={16} weight="fill" /> {busy ? "SIGNING…" : "Sign off & close"}
                    </button>
                  ) : (
                    <span className="text-xs text-navy/65" data-testid="close-admin-note">Only administrators can sign off.</span>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <span className="overline text-navy/70 block mb-1.5">{label}</span>
      {children}
    </div>
  );
}
